import { forwardRef, type ComponentType, type ReactNode } from 'react';
import { View } from '@amzn/stencil-react-components/layout';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';

interface TriggerProps {
  readonly children: ReactNode;
  readonly ariaLabel?: string;
  readonly role?: string;
  readonly wrapsDisabledControl: boolean;
}

/** A disabled `<button>` doesn't reliably receive hover events, so when the
 *  trigger wraps one, the control ignores the pointer and the wrapper takes it.
 *  Module-level so the style object is reference-stable. */
const DISABLED_CHILD_CSS = { '& button:disabled': { pointerEvents: 'none' } } as const;

/**
 * The element the tooltip anchors to. forwardRef so Stencil's `withTooltip`
 * can attach its trigger ref. Always a keyboard focus target (`tabIndex=0`):
 * what it wraps can't take focus itself (an icon, or a disabled button), so
 * without this the tooltip text would be mouse-only (WCAG 1.4.13 — Content on
 * Hover or Focus).
 */
const Trigger = forwardRef<HTMLDivElement, TriggerProps>(function Trigger(
  { children, ariaLabel, role, wrapsDisabledControl },
  ref,
) {
  return (
    <View
      ref={ref}
      display="inline-flex"
      tabIndex={0}
      {...(role !== undefined ? { role } : {})}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      {...(wrapsDisabledControl ? { css: DISABLED_CHILD_CSS } : {})}
    >
      {children}
    </View>
  );
});

// withTooltip's return type resolves through @amzn/stencil-react-desc, which
// the strict lint/type config sees as an unresolved (error) type — so the
// HOC application isn't callable/assignable without help. The HOC contract is
// stable, so treat it as a factory that wraps a component and adds the tooltip
// props we pass. This is the single place that cast lives.
type TooltipHoc = () => (
  c: ComponentType<TriggerProps>,
) => ComponentType<TriggerProps & TooltipExtraProps>;

interface TooltipExtraProps {
  readonly tooltipText: string;
  readonly isInline?: boolean;
  readonly tooltipDataTestId?: string;
}

const TriggerWithTooltip = (withTooltip as unknown as TooltipHoc)()(Trigger);

export interface TooltipTriggerProps {
  /** The text revealed on hover/focus. */
  readonly text: string;
  /**
   * What the tooltip anchors to: content that can't take focus itself (an
   * icon, a disabled button, …). The trigger supplies the tab stop, so don't
   * wrap already-focusable content (that would create a double tab stop).
   */
  readonly children: ReactNode;
  /** Accessible name for the trigger, when the content doesn't carry one. */
  readonly ariaLabel?: string;
  /** ARIA role for the trigger (e.g. `img` for a bare icon). */
  readonly role?: string;
  /**
   * Set when `children` is a disabled control: the wrapper becomes the
   * hover + keyboard target so the tooltip still explains why it's disabled.
   */
  readonly wrapsDisabledControl?: boolean;
  readonly dataTestId?: string;
}

/**
 * Wraps content in a Stencil tooltip that reveals `text` on hover/focus. The
 * shared base for `InfoTooltip` (an "ⓘ" help icon) and "disabled, here's why"
 * affordances on locked controls. Reuses Stencil's `withTooltip` so
 * positioning, focus handling, and portal behavior match the design system.
 */
export function TooltipTrigger({
  text,
  children,
  ariaLabel,
  role,
  wrapsDisabledControl = false,
  dataTestId,
}: TooltipTriggerProps): JSX.Element {
  return (
    <TriggerWithTooltip
      tooltipText={text}
      isInline
      wrapsDisabledControl={wrapsDisabledControl}
      {...(ariaLabel !== undefined ? { ariaLabel } : {})}
      {...(role !== undefined ? { role } : {})}
      {...(dataTestId !== undefined ? { tooltipDataTestId: dataTestId } : {})}
    >
      {children}
    </TriggerWithTooltip>
  );
}
